<template>
  <span
    class="skill"
    data-test-id="core-skill-element"
    :data-index="`${section}.${index}`"
    :data-name="skill.name"
    @keydown.enter.stop.prevent="addSkill"
    @keydown.delete.stop="removeSkill"
  >
    <span
      v-editable="isEditable"
      data-test-id="core-skill-name"
      :data-target-path="`resume.${section}.${index}.name`"
      class="name"
      v-text="skill.name"
    ></span>

    <ItemActions
      data-test-id="core-skill-actions"
      v-show="isEditable"
      :section="section"
      :index="index"
      :actions="['delete']"
      :confirm-delete="false"
    />
  </span>
</template>

<script setup lang="ts">
import { Resume } from '@/types/resume/v1'
import { nextTick, Ref, ref } from 'vue'
import ItemActions from './ItemActions.vue'

const emit = defineEmits(['add', 'remove'])

const { section, index } = defineProps<{
  skill: Resume.Skill
  index: number
  section: string
  isEditable?: boolean
}>()

const coreSkill: Ref<HTMLElement | null> = ref(null)

const addSkill = async event => {
  emit('add', index)
  await nextTick()

  const nextItem = event.currentTarget?.parentElement
    ?.querySelector(`span.skill[data-index="${section}.${index + 1}"]`)
    ?.querySelector('span')

  if (nextItem) {
    focusElement(nextItem)
  }
}

const removeSkill = event => {
  if (event.currentTarget?.querySelector('span')?.innerText.trim().length > 0) {
    return
  }
  event.preventDefault()
  const previousItem = event.currentTarget?.parentElement
    ?.querySelector(`span.skill[data-index="${section}.${index - 1}"]`)
    ?.querySelector('span')

  setCursor(previousItem)
  emit('remove', index)
}

const setCursor = element => {
  if (!element) return
  element.focus()
  const range = document.createRange()
  const selection = window.getSelection()
  range.setStart(element, element.childNodes.length)
  range.setEnd(element, element.childNodes.length)
  range.collapse(true)
  selection?.removeAllRanges()
  selection?.addRange(range)
}

const focusElement = element => {
  element.focus()
  const selection = window.getSelection()
  selection?.removeAllRanges()
  const range = document.createRange()
  range.selectNodeContents(element)
  selection?.addRange(range)
}
</script>

<style lang="postcss" scoped>
.skill {
  @apply text-sm-em leading-[inherit] inline-block mr-1-em relative min-w-[6px] h-auto;
  &:not(:last-child) {
    &:after {
      content: ',';
    }
  }
  .itemActions {
    @apply hidden;
  }
  &:hover {
    .itemActions {
      @apply flex;
    }
  }
}
</style>
